import axios from "axios";
import type { AxiosInstance } from 'axios'
import { loadAndApplyCustomerTheme } from "~/utils/theme";
import { isTokenExpired, clearSession } from "~/utils/session";

let keurtApiClient: AxiosInstance | null = null;

const setupKeurtApiClient = (baseURL: string) => {
  if (keurtApiClient) {
    throw new Error("keurtApiClient is already initialized. Setup can only be called once.");
  }

  keurtApiClient = axios.create({ baseURL });

  keurtApiClient.interceptors.request.use(
    (config: any) => {
      const token = useCookie("access_token").value;
      if (token && config.headers) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    (error:any) => {
      return Promise.reject(error);
    }
  );

  // Session expired while the page was open -> back to login.
  // Only when the token is really expired: some endpoints also return 401 for "no permission".
  keurtApiClient.interceptors.response.use(
    (response) => response,
    (error: any) => {
      if (error?.response?.status === 401 && isTokenExpired(useCookie("access_token").value)) {
        clearSession();
        navigateTo("/login");
      }
      return Promise.reject(error);
    }
  );
};

const getKeurtApiClient = (): AxiosInstance => {
  if (!keurtApiClient) {
    throw new Error("keurtApiClient is not set up. Please call setupKeurtApiClient() first.");
  }
  return keurtApiClient;
};


const storeAuthData = async (client: AxiosInstance, data: any) => {
  const accessToken = useCookie("access_token");
  accessToken.value = data.accessToken;
  // useCookie writes document.cookie in a watcher (next tick); wait for it so the
  // request interceptor sees the new token.
  await nextTick();
  sessionStorage.setItem("user", JSON.stringify(data.user));
  try {
    await loadAndApplyCustomerTheme(client, data.accessToken);
  } catch (err) {
    // A missing theme must never block the login.
    console.warn("Could not load customer theme", err);
  }
};

/** Result of a password login. "sso" means the account must log in via Microsoft. */
type LoginResult = "ok" | "invalid" | "sso";

const login = async (username: string, password: string): Promise<LoginResult> => {
  const client = getKeurtApiClient();
  try {
    const authDataResponse = await client.post("/login", {
      email: username,
      password: password,
    });

    if (authDataResponse.status === 200) {
      await storeAuthData(client, authDataResponse.data);
      return "ok";
    }
    return "invalid";
  }
  catch (err: any) {
    if (err?.response?.data?.sso) return "sso";
    return "invalid";
  }
};

/** Asks the API whether this e-mail address belongs to a customer with SSO. */
const ssoDiscover = async (email: string): Promise<{ sso: boolean; enforced: boolean }> => {
  try {
    const client = getKeurtApiClient();
    const response = await client.get("/sso/discover", { params: { email } });
    return { sso: !!response.data?.sso, enforced: !!response.data?.enforced };
  }
  catch {
    return { sso: false, enforced: false };
  }
};

const SSO_VERIFIER_KEY = "ssoVerifier";

const base64Url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

/**
 * Full URL to start the Microsoft login (browser navigation, not an XHR call).
 * Generates a random verifier that stays in this tab (sessionStorage); only its SHA-256 goes to the API.
 * /sso/exchange requires the verifier, so the login can only be completed in the browser that started it.
 */
const ssoStartUrl = async (email: string): Promise<string> => {
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
  sessionStorage.setItem(SSO_VERIFIER_KEY, verifier);
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  const challenge = base64Url(new Uint8Array(digest));

  const client = getKeurtApiClient();
  const base = (client.defaults.baseURL || "").replace(/\/+$/, "");
  return `${base}/sso/start?email=${encodeURIComponent(email)}&challenge=${encodeURIComponent(challenge)}`;
};

/** Exchanges the one-time code from /ssoCallback for the normal Keurt token. */
const ssoExchange = async (code: string): Promise<boolean> => {
  const verifier = sessionStorage.getItem(SSO_VERIFIER_KEY);
  sessionStorage.removeItem(SSO_VERIFIER_KEY);
  if (!verifier) return false;

  try {
    const client = getKeurtApiClient();
    const response = await client.post("/sso/exchange", { code, verifier });
    if (response.status === 200) {
      await storeAuthData(client, response.data);
      return true;
    }
    return false;
  }
  catch {
    return false;
  }
};

export { setupKeurtApiClient, getKeurtApiClient, login, ssoDiscover, ssoStartUrl, ssoExchange };
export type { LoginResult };
